import { useCallback, useSyncExternalStore } from 'react'
import {
  findUserUiPreferences,
  patchUserUiPreferences,
  type PatchUserUiPreferencesPayload,
} from '@/api'
import { useAuth } from '@/hooks/use-auth'
import { useSettings } from '@/hooks/use-settings'
import {
  localOverridesWhenServerIsDefault,
  normalizeUiPreferences,
  toInterfaceVariant,
  writeStoredDeclarationVariant,
  type NormalizedUiPreferences,
} from '@/lib/userUiPreferences'
import {
  applyUiPreferencesPatch,
  getUiPreferencesHydrated,
  getUiPreferencesSnapshot,
  replaceUiPreferencesSnapshot,
  subscribeUiPreferences,
} from '@/lib/userUiPreferencesStore'
import type { ThemePalettePreference } from '@/types/settings'

function applyLocalSideEffects(
  preferences: NormalizedUiPreferences,
  handleUpdate: (settings: { paletteMode: ThemePalettePreference }) => void
) {
  handleUpdate({ paletteMode: preferences.theme })
  writeStoredDeclarationVariant(toInterfaceVariant(preferences.declarationInterface))
}

export function useUserUiPreferences() {
  const preferences = useSyncExternalStore(
    subscribeUiPreferences,
    getUiPreferencesSnapshot,
    getUiPreferencesSnapshot
  )
  const hydrated = useSyncExternalStore(
    subscribeUiPreferences,
    getUiPreferencesHydrated,
    getUiPreferencesHydrated
  )
  const { user } = useAuth()
  const { handleUpdate } = useSettings()

  const update = useCallback(
    async (patch: PatchUserUiPreferencesPayload) => {
      const optimistic = applyUiPreferencesPatch(patch)
      if (patch.theme) handleUpdate({ paletteMode: optimistic.theme })
      if (patch.declarationInterface) {
        writeStoredDeclarationVariant(toInterfaceVariant(optimistic.declarationInterface))
      }
      if (!user?.uid) return optimistic
      try {
        const saved = normalizeUiPreferences(
          await patchUserUiPreferences(user.uid, patch)
        )
        replaceUiPreferencesSnapshot(saved)
        applyLocalSideEffects(saved, handleUpdate)
        return saved
      } catch {
        return optimistic
      }
    },
    [handleUpdate, user?.uid]
  )

  const hydrate = useCallback(async () => {
    if (!user?.uid) return
    const server = normalizeUiPreferences(await findUserUiPreferences(user.uid))
    const migration = localOverridesWhenServerIsDefault(server)
    const next = migration
      ? normalizeUiPreferences(await patchUserUiPreferences(user.uid, migration))
      : server
    replaceUiPreferencesSnapshot(next)
    applyLocalSideEffects(next, handleUpdate)
  }, [handleUpdate, user?.uid])

  return { preferences, hydrated, update, hydrate }
}
